/**
 * Formatting for the MF Overview: money, counts, units, dates and statuses.
 */

import type { MFRecord } from "../../../Api/mfAdminInvestments";

/** A number out of a number or a numeric string ("1200.00"); null otherwise. */
export const toNumber = (value: unknown): number | null => {
  if (typeof value === "number") return Number.isFinite(value) ? value : null;
  if (typeof value === "string" && value.trim() && /^-?[\d,]*\.?\d+$/.test(value.trim())) {
    const parsed = Number(value.replace(/,/g, ""));
    return Number.isFinite(parsed) ? parsed : null;
  }
  return null;
};

/**
 * The amount exactly as the API gave it, to at most two decimals: ₹9,72,600,
 * ₹9,839.06. Paise are shown only when there are any. Money on this page is
 * never shortened to K / L / Cr — admins read these figures as the real ones.
 */
export const formatMoneyExact = (value: unknown): string => {
  const amount = toNumber(value);
  if (amount === null) return "—";
  return new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    minimumFractionDigits: Number.isInteger(amount) ? 0 : 2,
    maximumFractionDigits: 2,
  }).format(amount);
};

export const formatCount = (value: unknown): string => {
  const count = toNumber(value);
  return count === null ? "—" : new Intl.NumberFormat("en-IN").format(count);
};

export const formatUnits = (value: unknown): string => {
  const units = toNumber(value);
  return units === null
    ? "—"
    : new Intl.NumberFormat("en-IN", { maximumFractionDigits: 4 }).format(units);
};

export const formatDate = (value?: string | null): string => {
  if (!value) return "—";
  const date = new Date(value);
  return Number.isNaN(date.getTime())
    ? String(value)
    : date.toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
};

export const formatDateTime = (value?: string | null): string => {
  if (!value) return "—";
  const date = new Date(value);
  return Number.isNaN(date.getTime())
    ? String(value)
    : date.toLocaleString("en-IN", {
        day: "2-digit",
        month: "short",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
};

/** When a record was created, under whichever name it carries it. */
export const createdAtOf = (row: MFRecord): string | null =>
  row.created_at || row.inserted_at || row.insertedAt || null;

const ACRONYMS: Record<string, string> = {
  sip: "SIP",
  sips: "SIPs",
  swp: "SWP",
  swps: "SWPs",
  stp: "STP",
  stps: "STPs",
  mf: "MF",
  aum: "AUM",
  nav: "NAV",
  amc: "AMC",
  id: "ID",
  inr: "INR",
  xirr: "XIRR",
  kyc: "KYC",
};

/** "total_sip_amount" or "totalSipAmount" → "Total SIP amount". */
export const humanizeKey = (key: string): string => {
  const words = key
    .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
    .replace(/[_\-.]+/g, " ")
    .trim()
    .toLowerCase()
    .split(/\s+/)
    .filter(Boolean)
    .map((word) => ACRONYMS[word] || word);
  if (!words.length) return key;
  const first = words[0];
  words[0] = first === first.toUpperCase() ? first : first[0].toUpperCase() + first.slice(1);
  return words.join(" ");
};
